import React from 'react';
import {
    Avatar,
    Box,
    Typography,
    makeStyles,
} from '@material-ui/core';
import { Card, Button } from 'src/shared/ui';
import DeleteIcon from '@material-ui/icons/Delete';
import PersonIcon from '@material-ui/icons/Person';
import { useTranslation } from 'react-i18next';
import type { TServiceClientHistory } from 'src/interfaces/serviceClient';
import { CapitalizeNames } from 'src/utils/capitalize';
import { formatCalendarDateUTC } from 'src/utils/formatDate';
import { Label } from 'src/components/Tags';
import numeral from 'numeral';

const useStyles = makeStyles((theme) => ({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2.5),
    },
    author: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    avatar: {
        width: 36,
        height: 36,
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
        fontWeight: 700,
    },
    advisorName: {
        fontWeight: 700,
        letterSpacing: -0.2,
    },
    dateChip: {
        backgroundColor: '#d7d2ff',
        color: '#3b3561',
        fontWeight: 600,
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.75),
    },
    sectionTitle: {
        fontWeight: 700,
    },
    listItem: {
        paddingTop: 0,
        paddingBottom: theme.spacing(0.5),
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    deleteButton: {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.common.white,
        fontWeight: 700,
        textTransform: 'uppercase',
        '&:hover': {
            backgroundColor: theme.palette.error.dark,
        },
    },
    emptyCard: {
        padding: theme.spacing(3),
    },
}));

type ServiceHistoryCardsProps = {
    history: TServiceClientHistory[];
    canDelete?: boolean;
    onDelete?: (serviceHistoryId: string) => void;
};

const getServiceName = (
    service: TServiceClientHistory,
    fallbackServiceName: string
): string => service.maintenancePackage?.name || fallbackServiceName;

const getAdvisorName = (
    service: TServiceClientHistory,
    fallbackAdvisorName: string
): string => {
    const fullName = String(service.user?.name || '').trim();

    return fullName ? CapitalizeNames(fullName) : fallbackAdvisorName;
};

export const ServiceHistoryCards = ({
    history,
    canDelete,
    onDelete,
}: ServiceHistoryCardsProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const fallbackServiceName = String(t('serviceClient.ServiceFallback'));
    const fallbackAdvisorName = String(t('serviceClient.AdvisorFallback'));

    return (
        <Box className={classes.wrapper} data-testid="service-history-cards">
            {history.map((service, index) => {
                const serviceDetails = [
                    ...(service.details || []),
                    ...(service.additionalDetails || []),
                ];
                const canDeleteItem = Boolean(
                    canDelete && service._id && onDelete
                );

                return (
                    <Card key={`${service.serviceDate}-${index}`}>
                        <Box className={classes.body}>
                            <Box className={classes.cardHeader}>
                                <Box className={classes.author}>
                                    <Avatar
                                        className={classes.avatar}
                                        src={
                                            'https://automotive-api.s3.us-east-2.amazonaws.com/' +
                                            service.user?.image || undefined
                                        }
                                    >
                                        <PersonIcon fontSize="small" />
                                    </Avatar>
                                    <Typography
                                        variant="h4"
                                        className={classes.advisorName}
                                    >
                                        {getAdvisorName(
                                            service,
                                            fallbackAdvisorName
                                        )}
                                    </Typography>
                                </Box>
                                <Label color="primary">
                                    <p>
                                        {formatCalendarDateUTC(
                                            service.serviceDate
                                        )}
                                    </p>
                                </Label>
                            </Box>

                            <Box
                                display="flex"
                                flexDirection="column"
                                gridGap={5}
                            >
                                <Typography
                                    variant="h5"
                                    style={{ fontWeight: 700 }}
                                >
                                    {getServiceName(
                                        service,
                                        fallbackServiceName
                                    )}
                                </Typography>
                                <Typography variant="body2">
                                    {service.description || '— - -'}
                                </Typography>
                            </Box>

                            {serviceDetails.length > 0 && (
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    gridGap={5}
                                >
                                    <Typography
                                        variant="h5"
                                        style={{ fontWeight: 700 }}
                                    >
                                        {String(
                                            t(
                                                'serviceClient.ServiceDetailsTitle'
                                            )
                                        )}
                                    </Typography>
                                    {serviceDetails.map(
                                        (detail, detailIndex) => (
                                            <Typography
                                                variant="body2"
                                                key={`${detail.title}-${detailIndex}`}
                                            >
                                                {`• ${detail.title ||
                                                    detail.description
                                                    }`}
                                            </Typography>
                                        )
                                    )}
                                </Box>
                            )}
                            <Box
                                display="flex"
                                flexDirection="column"
                                gridGap={5}
                            >
                                <Typography
                                    variant="h5"
                                    style={{ fontWeight: 700 }}
                                >
                                    {String(t('serviceClient.Cost'))}
                                </Typography>
                                <Typography variant="body2">
                                    {numeral(service.price).format('$0,0.00')}
                                </Typography>
                            </Box>
                            <Box
                                display="flex"
                                flexDirection="column"
                                gridGap={5}
                            >
                                <Typography
                                    variant="h5"
                                    style={{ fontWeight: 700 }}
                                >
                                    {String(t('serviceClient.DeliveryDate'))}
                                </Typography>
                                <Typography variant="body2">
                                    {formatCalendarDateUTC(
                                        service.deliveryDate
                                    )}
                                </Typography>
                            </Box>

                            {canDelete && (
                                <Box className={classes.actions}>
                                    <Button
                                        variant="contained"
                                        startIcon={<DeleteIcon />}
                                        disabled={!canDeleteItem}
                                        intent="error"
                                        onClick={() =>
                                            canDeleteItem &&
                                            onDelete?.(service._id)
                                        }
                                    >
                                        {String(t('Buttons.Delete'))}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Card>
                );
            })}
        </Box>
    );
};
